<template>
  <main class="site-page relative min-h-screen overflow-hidden bg-[var(--page)] text-[var(--text)] antialiased" :style="{
    '--pointer-x': `${pointer.x}%`,
    '--pointer-y': `${pointer.y}%`
  }" @pointermove="handlePointerMove">
    <!-- AMBIENTE GLOBAL -->
    <div class="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
      <div class="ambient-grid absolute inset-0"></div>
      <div class="ambient-noise absolute inset-0"></div>
      <div class="pointer-glow absolute inset-0"></div>

      <div class="ambient-orb ambient-orb-one"></div>
      <div class="ambient-orb ambient-orb-two"></div>
      <div class="ambient-orb ambient-orb-three"></div>
    </div>

    <!-- VOLTAR AO TOPO -->
    <transition name="scroll-top">
      <button v-show="showScrollTop" type="button"
        class="scroll-top-button fixed bottom-5 right-5 z-50 grid h-11 w-11 place-items-center rounded-full border border-[var(--border)] bg-[var(--glass-strong)] text-[var(--text-strong)] shadow-[var(--shadow-soft)] backdrop-blur-2xl transition duration-300 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--focus)]"
        aria-label="Voltar ao topo" title="Voltar ao topo" @click="scrollToTop">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
          class="h-5 w-5" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 15l6-6 6 6" />
        </svg>
      </button>
    </transition>

    <!-- HERO -->
    <section class="relative z-10">
      <div class="mx-auto max-w-7xl px-5 pb-8 pt-20 sm:px-6 lg:px-8 lg:pt-28">
        <div class="mx-auto max-w-5xl text-center" data-reveal>
          <div
            class="inline-flex items-center gap-2 rounded-full border border-[var(--border)] bg-[var(--glass)] px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--muted)] shadow-[var(--shadow-soft)] backdrop-blur-2xl">
            <span class="relative flex h-2 w-2">
              <span
                class="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-50"></span>
              <span class="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
            </span>

            Grivaro Studio

            <span class="hidden text-[var(--muted-2)] sm:inline">
              / Product Engineering
            </span>
          </div>

          <h1
            class="mx-auto mt-7 max-w-5xl text-5xl font-semibold tracking-[-0.065em] text-[var(--text-strong)] sm:text-6xl lg:text-[5.7rem] lg:leading-[0.96]">
            Software sob medida.
            <span class="hero-gradient block">
              Simples onde importa.
            </span>
          </h1>

          <p class="mx-auto mt-7 max-w-3xl text-lg leading-8 text-[var(--muted)] sm:text-xl">
            Produtos digitais, sistemas internos e integrações com uma experiência
            clara por fora e uma arquitetura sólida por dentro.
          </p>

          <div class="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a :href="whatsHref" target="_blank" rel="noopener"
              class="primary-button group inline-flex h-12 items-center justify-center gap-2 rounded-full px-7 text-sm font-semibold transition duration-300 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--focus)]">
              Começar projeto

              <span class="transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden="true">
                →
              </span>
            </a>

            <router-link to="/solutions"
              class="secondary-button inline-flex h-12 items-center justify-center rounded-full px-7 text-sm font-semibold transition duration-300 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--focus)]">
              Explorar soluções
            </router-link>
          </div>
        </div>

        <!-- MOCKUP -->
        <div class="relative mx-auto mt-16 max-w-6xl lg:mt-20" data-reveal>
          <div class="hero-halo absolute inset-x-12 top-12 h-48 rounded-full blur-3xl"></div>

          <div class="device-frame relative rounded-[2.2rem] p-2.5 sm:rounded-[2.8rem] sm:p-3">
            <div
              class="overflow-hidden rounded-[1.75rem] border border-white/10 bg-[#08090b] text-white sm:rounded-[2.3rem]">
              <!-- browser -->
              <div class="flex items-center justify-between border-b border-white/[0.07] px-5 py-4 sm:px-6">
                <div class="flex items-center gap-2">
                  <span class="h-2.5 w-2.5 rounded-full bg-white/20"></span>
                  <span class="h-2.5 w-2.5 rounded-full bg-white/20"></span>
                  <span class="h-2.5 w-2.5 rounded-full bg-white/20"></span>
                </div>

                <div
                  class="hidden items-center gap-2 rounded-full bg-white/[0.06] px-4 py-2 text-[10px] font-medium text-zinc-400 ring-1 ring-white/10 sm:flex">
                  <span class="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
                  Grivaro / live-system
                </div>

                <span class="rounded-full bg-white/[0.07] px-3 py-1.5 text-[10px] font-semibold text-zinc-300">
                  Live
                </span>
              </div>

              <div class="grid min-h-[34rem] lg:grid-cols-[13rem_1fr]">
                <aside class="hidden border-r border-white/[0.07] p-5 lg:block">
                  <div class="flex items-center gap-3">
                    <div class="grid h-9 w-9 place-items-center rounded-xl bg-white text-xs font-black text-black">
                      G
                    </div>

                    <div>
                      <p class="text-xs font-semibold text-white">
                        Grivaro OS
                      </p>

                      <p class="mt-0.5 text-[10px] text-zinc-500">
                        Workspace
                      </p>
                    </div>
                  </div>

                  <div class="mt-8 space-y-2">
                    <div v-for="(item, index) in mockMenu" :key="item" :class="[
                      'rounded-xl px-3 py-2.5 text-xs font-medium',
                      index === 0
                        ? 'bg-white text-black'
                        : 'text-zinc-500'
                    ]">
                      {{ item }}
                    </div>
                  </div>

                  <div class="mt-10 rounded-2xl bg-white/[0.04] p-4 ring-1 ring-white/[0.07]">
                    <p class="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">
                      System
                    </p>

                    <div class="mt-3 flex items-center gap-2">
                      <span class="h-2 w-2 rounded-full bg-emerald-400"></span>

                      <span class="text-xs text-zinc-300">
                        Operação estável
                      </span>
                    </div>
                  </div>
                </aside>

                <div class="min-w-0 p-5 sm:p-7 lg:p-8">
                  <div class="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <p class="text-xs font-medium text-zinc-500">
                        Product Operating System
                      </p>

                      <h2 class="mt-2 text-2xl font-semibold tracking-[-0.035em] sm:text-3xl">
                        Build, launch and scale.
                      </h2>
                    </div>

                    <div
                      class="inline-flex w-fit items-center gap-2 rounded-full bg-white/[0.06] px-3 py-2 text-[10px] font-semibold text-zinc-300 ring-1 ring-white/10">
                      <span class="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>

                      Architecture online
                    </div>
                  </div>

                  <div class="mt-9 grid gap-4 sm:grid-cols-3">
                    <article v-for="metric in metrics" :key="metric.label" class="dashboard-card rounded-[1.4rem] p-5">
                      <p class="text-2xl font-semibold tracking-[-0.035em]">
                        {{ metric.value }}
                      </p>

                      <p class="mt-2 text-xs leading-5 text-zinc-500">
                        {{ metric.label }}
                      </p>
                    </article>
                  </div>

                  <div class="mt-4 grid gap-4 xl:grid-cols-[1.35fr_.65fr]">
                    <article class="dashboard-card rounded-[1.6rem] p-5 sm:p-6">
                      <div class="flex items-center justify-between">
                        <div>
                          <p class="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">
                            Performance
                          </p>

                          <p class="mt-2 text-sm font-semibold text-white">
                            Operação nos últimos ciclos
                          </p>
                        </div>

                        <span class="text-xs font-semibold text-emerald-400">
                          +18.4%
                        </span>
                      </div>

                      <div class="mt-8 flex h-40 items-end gap-2">
                        <span v-for="(height, index) in chartBars" :key="index" class="chart-bar flex-1 rounded-t-md"
                          :style="{
                            height: `${height}%`,
                            animationDelay: `${index * 45}ms`
                          }"></span>
                      </div>

                      <div class="mt-4 flex justify-between text-[9px] font-medium text-zinc-600">
                        <span>JAN</span>
                        <span>MAR</span>
                        <span>MAI</span>
                        <span>JUL</span>
                        <span>SET</span>
                      </div>
                    </article>

                    <article class="dashboard-card rounded-[1.6rem] p-5 sm:p-6">
                      <p class="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">
                        Health
                      </p>

                      <div class="mt-6 grid place-items-center">
                        <div class="health-ring grid h-32 w-32 place-items-center rounded-full">
                          <div class="grid h-24 w-24 place-items-center rounded-full bg-[#0c0d10]">
                            <div class="text-center">
                              <p class="text-3xl font-semibold tracking-tight">
                                99.9
                              </p>

                              <p class="mt-1 text-[9px] uppercase tracking-widest text-zinc-500">
                                Uptime
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="mt-6 grid grid-cols-2 gap-2">
                        <div class="rounded-xl bg-white/[0.04] p-3 ring-1 ring-white/[0.06]">
                          <p class="text-[9px] text-zinc-500">
                            Latency
                          </p>

                          <p class="mt-1 text-xs font-semibold">
                            184ms
                          </p>
                        </div>

                        <div class="rounded-xl bg-white/[0.04] p-3 ring-1 ring-white/[0.06]">
                          <p class="text-[9px] text-zinc-500">
                            Errors
                          </p>

                          <p class="mt-1 text-xs font-semibold">
                            0.02%
                          </p>
                        </div>
                      </div>
                    </article>
                  </div>

                  <div class="mt-4 grid gap-3 sm:grid-cols-3">
                    <article v-for="item in productHighlights" :key="item.title"
                      class="dashboard-card rounded-[1.3rem] p-4">
                      <div class="mb-3 h-1 w-8 rounded-full bg-white/25"></div>

                      <p class="text-xs font-semibold text-white">
                        {{ item.title }}
                      </p>

                      <p class="mt-1.5 text-[11px] leading-5 text-zinc-500">
                        {{ item.desc }}
                      </p>
                    </article>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- pequena ponte inferior do hero -->
          <div
            class="hero-dock relative z-20 mx-auto mt-5 w-[92%] rounded-[1.5rem] border border-[var(--border)] bg-[var(--glass-strong)] p-2.5 shadow-[var(--shadow-soft)] backdrop-blur-2xl sm:mt-6 sm:w-[86%]">
            <div class="grid gap-2 sm:grid-cols-3">
              <div v-for="item in heroDock" :key="item.title"
                class="flex items-center gap-3 rounded-[1rem] px-3 py-3 transition hover:bg-[var(--chip)]">
                <div
                  class="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[var(--text-strong)] text-[10px] font-bold text-[var(--inverse)]">
                  {{ item.symbol }}
                </div>

                <div class="min-w-0">
                  <p class="truncate text-xs font-semibold text-[var(--text-strong)]">
                    {{ item.title }}
                  </p>

                  <p class="mt-0.5 truncate text-[10px] text-[var(--muted)]">
                    {{ item.desc }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- TECH RAIL -->
        <div class="mx-auto mt-8 max-w-5xl" data-reveal>
          <div
            class="tech-rail overflow-hidden rounded-full border border-[var(--border)] bg-[var(--glass)] py-3 backdrop-blur-xl">
            <div class="tech-track flex min-w-max items-center gap-3 px-3">
              <template v-for="loop in 2" :key="loop">
                <span v-for="tech in techBadges" :key="`${loop}-${tech}`"
                  class="rounded-full bg-[var(--chip)] px-4 py-2 text-xs font-semibold text-[var(--muted)] ring-1 ring-[var(--border)]">
                  {{ tech }}
                </span>
              </template>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CONECTOR HERO -> SERVIÇOS -->
    <div class="flow-transition relative z-10 mx-auto mt-14 max-w-7xl px-5 sm:px-6 lg:px-8">
      <div class="flow-line"></div>
      <div class="flow-node"></div>
    </div>

    <!-- SERVIÇOS -->
    <section class="relative z-10 py-20 sm:py-24">
      <div class="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div class="grid gap-10 lg:grid-cols-12 lg:items-end" data-reveal>
          <div class="lg:col-span-7">
            <p class="eyebrow">
              O que fazemos
            </p>

            <h2 class="section-title mt-4">
              Menos ruído.
              <span class="text-[var(--muted-2)]">
                Mais produto.
              </span>
            </h2>
          </div>

          <p class="text-lg leading-8 text-[var(--muted)] lg:col-span-5">
            Do primeiro desenho ao deploy, criamos sistemas que organizam
            processos, automatizam rotinas e ajudam empresas a operar melhor.
          </p>
        </div>

        <!-- bento solto -->
        <div class="mt-14 grid gap-4 md:grid-cols-2 xl:grid-cols-12 xl:items-start">
          <article v-for="(service, index) in services" :key="service.title" :class="[
            'service-card group relative overflow-hidden rounded-[2rem] border border-[var(--border)] bg-[var(--card)] p-7 transition duration-500',
            index === 0
              ? 'xl:col-span-7'
              : index === 1
                ? 'xl:col-span-5 xl:mt-12'
                : index === 2
                  ? 'xl:col-span-5 xl:-mt-4'
                  : 'xl:col-span-7 xl:mt-8'
          ]" data-reveal>
            <div class="card-shine"></div>

            <div class="relative">
              <div class="flex items-start justify-between gap-6">
                <div
                  class="grid h-12 w-12 place-items-center rounded-2xl border border-[var(--border)] bg-[var(--surface)] text-xs font-bold text-[var(--text-strong)] shadow-sm">
                  {{ service.symbol }}
                </div>

                <span class="text-2xl text-[var(--muted-2)] transition-transform duration-500 group-hover:translate-x-1"
                  aria-hidden="true">
                  ↗
                </span>
              </div>

              <h3 class="mt-10 text-2xl font-semibold tracking-[-0.035em] text-[var(--text-strong)]">
                {{ service.title }}
              </h3>

              <p class="mt-3 max-w-xl text-sm leading-7 text-[var(--muted)]">
                {{ service.desc }}
              </p>

              <div class="mt-7 flex flex-wrap gap-2">
                <span v-for="point in service.points" :key="point"
                  class="rounded-full border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-xs font-medium text-[var(--muted)]">
                  {{ point }}
                </span>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- TRANSIÇÃO CURVA -->
    <div class="relative z-10 mx-auto max-w-7xl px-5 sm:px-6 lg:px-8" aria-hidden="true">
      <div class="curve-transition">
        <span class="curve-dot"></span>
      </div>
    </div>

    <!-- PROCESSO -->
    <section class="relative z-10 py-16 sm:py-20">
      <div class="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div
          class="process-shell relative overflow-hidden rounded-[2.5rem] border border-[var(--border)] bg-[var(--glass)] p-5 shadow-[var(--shadow-large)] backdrop-blur-2xl sm:p-8 lg:p-10"
          data-reveal>
          <div class="process-glow" aria-hidden="true"></div>

          <div class="relative grid gap-12 lg:grid-cols-[.82fr_1.18fr] lg:items-start">
            <div class="lg:sticky lg:top-28">
              <p class="eyebrow">
                Método
              </p>

              <h2 class="mt-4 text-4xl font-semibold tracking-[-0.05em] text-[var(--text-strong)] sm:text-5xl">
                Uma linha contínua
                <span class="block text-[var(--muted-2)]">
                  da ideia ao lançamento.
                </span>
              </h2>

              <p class="mt-5 max-w-xl text-base leading-7 text-[var(--muted)]">
                Cada etapa existe para reduzir incerteza, manter prioridade clara
                e fazer o projeto avançar sem perder contexto.
              </p>

              <div
                class="mt-8 inline-flex items-center gap-3 rounded-full border border-[var(--border)] bg-[var(--surface)] px-4 py-3 text-xs font-semibold text-[var(--muted)]">
                <span class="h-2 w-2 rounded-full bg-emerald-500"></span>

                Entregas visíveis em ciclos curtos
              </div>
            </div>

            <div class="relative">
              <div class="timeline-line absolute bottom-10 left-6 top-6 w-px" aria-hidden="true"></div>

              <div class="space-y-4">
                <article v-for="(step, index) in process" :key="step.title" class="process-step group relative pl-16"
                  data-reveal>
                  <div
                    class="timeline-number absolute left-0 top-1 grid h-12 w-12 place-items-center rounded-full border border-[var(--border)] bg-[var(--surface)] text-xs font-bold text-[var(--text-strong)] shadow-[var(--shadow-soft)] transition duration-500 group-hover:scale-110 group-hover:bg-[var(--text-strong)] group-hover:text-[var(--inverse)]">
                    {{ String(index + 1).padStart(2, '0') }}
                  </div>

                  <div
                    class="rounded-[1.6rem] border border-[var(--border)] bg-[var(--card)] p-5 transition duration-500 group-hover:translate-x-1 group-hover:shadow-[var(--shadow-soft)] sm:p-6">
                    <p class="text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--muted-2)]">
                      Etapa {{ index + 1 }}
                    </p>

                    <h3 class="mt-2 text-xl font-semibold tracking-tight text-[var(--text-strong)]">
                      {{ step.title }}
                    </h3>

                    <p class="mt-3 text-sm leading-6 text-[var(--muted)]">
                      {{ step.desc }}
                    </p>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CASES -->
    <section class="relative z-10 py-20 sm:py-24">
      <div class="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div class="grid gap-10 lg:grid-cols-12 lg:items-end" data-reveal>
          <div class="lg:col-span-8">
            <p class="eyebrow">
              Selected work
            </p>

            <h2 class="section-title mt-4">
              Produtos reais.
              <span class="text-[var(--muted-2)]">
                Operação real.
              </span>
            </h2>
          </div>

          <div class="lg:col-span-4 lg:text-right">
            <router-link to="/projects"
              class="secondary-button inline-flex h-11 items-center justify-center gap-2 rounded-full px-5 text-sm font-semibold">
              Ver todos os projetos
              <span aria-hidden="true">→</span>
            </router-link>
          </div>
        </div>

        <div class="mt-14 grid gap-5 lg:grid-cols-12">
          <article v-for="(project, index) in cases" :key="project.title" :class="[
            'case-card group overflow-hidden rounded-[2rem] border border-[var(--border)] bg-[var(--card)] transition duration-500',
            index === 0
              ? 'lg:col-span-7'
              : index === 1
                ? 'lg:col-span-5 lg:mt-16'
                : 'lg:col-span-10 lg:col-start-2 lg:-mt-2'
          ]" data-reveal>
            <div :class="[
              'grid',
              index === 2
                ? 'lg:grid-cols-[1.08fr_.92fr]'
                : ''
            ]">
              <div :class="[
                'relative overflow-hidden bg-[var(--chip)]',
                index === 2
                  ? 'aspect-[16/10] lg:aspect-auto lg:min-h-[24rem]'
                  : 'aspect-[16/10]'
              ]">
                <img :src="project.image" :alt="project.title"
                  class="h-full w-full object-cover transition duration-1000 ease-out group-hover:scale-[1.035]"
                  loading="lazy" />

                <div class="case-overlay absolute inset-0"></div>
              </div>

              <div class="p-7 sm:p-8">
                <div class="flex items-center justify-between gap-3">
                  <span
                    class="rounded-full border border-[var(--border)] bg-[var(--surface)] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--muted)]">
                    {{ project.kicker }}
                  </span>

                  <span
                    class="text-xl text-[var(--muted-2)] transition-transform duration-500 group-hover:translate-x-1">
                    ↗
                  </span>
                </div>

                <h3 class="mt-6 text-3xl font-semibold tracking-[-0.04em] text-[var(--text-strong)]">
                  {{ project.title }}
                </h3>

                <p class="mt-4 text-sm leading-7 text-[var(--muted)]">
                  {{ project.desc }}
                </p>

                <div class="mt-7 grid gap-2">
                  <div v-for="win in project.wins" :key="win"
                    class="flex items-center gap-3 rounded-xl border border-[var(--border)] bg-[var(--surface)] px-4 py-3 text-xs font-semibold text-[var(--muted)]">
                    <span class="h-1.5 w-1.5 rounded-full bg-[var(--text-strong)]"></span>

                    {{ win }}
                  </div>
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- TRANSIÇÃO ESCURA -->
    <div class="relative z-10 mx-auto -mb-8 mt-4 flex max-w-7xl justify-center" aria-hidden="true">
      <div
        class="dark-transition-pill rounded-full border border-[var(--border)] bg-[var(--glass-strong)] px-5 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--muted)] shadow-[var(--shadow-soft)] backdrop-blur-xl">
        Product thinking · Engineering · Evolution
      </div>
    </div>

    <!-- SOB MEDIDA -->
    <section class="relative z-10 py-16 sm:py-20">
      <div class="mx-auto max-w-[88rem] px-3 sm:px-5 lg:px-7">
        <div
          class="dark-panel relative overflow-hidden rounded-[2.7rem] px-5 py-16 text-white shadow-[var(--shadow-large)] sm:px-8 sm:py-20 lg:px-12 xl:px-16">
          <div class="dark-panel-grid pointer-events-none absolute inset-0" aria-hidden="true"></div>

          <div class="dark-orb absolute -right-24 -top-28 h-96 w-96 rounded-full blur-3xl" aria-hidden="true"></div>

          <div class="relative grid gap-12 lg:grid-cols-12 lg:items-center">
            <div class="lg:col-span-7" data-reveal>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
                Sob medida
              </p>

              <h2 class="mt-5 max-w-3xl text-4xl font-semibold tracking-[-0.05em] sm:text-5xl lg:text-6xl">
                Seu processo não precisa caber em um sistema genérico.
              </h2>

              <p class="mt-6 max-w-2xl text-lg leading-8 text-zinc-400">
                A tecnologia se adapta ao fluxo da operação — não o contrário.
                Isso deixa o produto mais claro hoje e mais fácil de evoluir amanhã.
              </p>
            </div>

            <div class="grid gap-3 lg:col-span-5">
              <article v-for="principle in principles" :key="principle.title"
                class="principle-card rounded-[1.5rem] bg-white/[0.055] p-5 ring-1 ring-white/10 backdrop-blur-xl transition duration-500 hover:translate-x-1 hover:bg-white/[0.085]"
                data-reveal>
                <div class="flex gap-4">
                  <span class="text-xs font-bold text-zinc-600">
                    {{ principle.kicker }}
                  </span>

                  <div>
                    <h3 class="text-lg font-semibold">
                      {{ principle.title }}
                    </h3>

                    <p class="mt-2 text-sm leading-6 text-zinc-400">
                      {{ principle.desc }}
                    </p>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- STACK + CTA CONECTADOS POR SOBREPOSIÇÃO -->
    <section class="relative z-10 pb-24 pt-20 sm:pb-28 sm:pt-24">
      <div class="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div class="mx-auto max-w-3xl text-center" data-reveal>
          <p class="eyebrow">
            Stack
          </p>

          <h2 class="section-title mt-4">
            Tecnologia moderna.
            <span class="text-[var(--muted-2)]">
              Escolhida pelo problema.
            </span>
          </h2>
        </div>

        <div class="mx-auto mt-14 grid max-w-5xl gap-4 md:grid-cols-3">
          <article v-for="(stack, index) in stackGroups" :key="stack.title" :class="[
            'stack-card rounded-[1.8rem] border border-[var(--border)] bg-[var(--card)] p-6 transition duration-500',
            index === 1 ? 'md:-translate-y-5' : ''
          ]" data-reveal>
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-semibold text-[var(--text-strong)]">
                {{ stack.title }}
              </h3>

              <span class="text-[var(--muted-2)]">
                ⌘
              </span>
            </div>

            <div class="mt-6 flex flex-wrap gap-2">
              <span v-for="item in stack.items" :key="item"
                class="tech-pill rounded-full border border-[var(--border)] bg-[var(--surface)] px-3.5 py-2 text-xs font-semibold text-[var(--muted)] transition duration-300">
                {{ item }}
              </span>
            </div>
          </article>
        </div>

        <!-- CONECTOR STACK -> CTA -->
        <div class="stack-cta-bridge relative mx-auto mt-5 h-12 max-w-6xl" aria-hidden="true">
          <span class="stack-cta-line"></span>
          <span class="stack-cta-node"></span>
        </div>

        <!-- CTA conectado, mas com respiro -->
        <div
          class="cta-panel relative mx-auto mt-2 max-w-6xl overflow-hidden rounded-[2.5rem] border border-[var(--border)] px-5 py-12 shadow-[var(--shadow-large)] sm:px-8 sm:py-14 lg:px-12"
          data-reveal>
          <div class="cta-orb absolute -right-24 -top-24 h-72 w-72 rounded-full blur-3xl" aria-hidden="true"></div>

          <div class="relative grid gap-10 lg:grid-cols-12 lg:items-center">
            <div class="lg:col-span-8">
              <p class="eyebrow">
                Próximo passo
              </p>

              <h2
                class="mt-4 max-w-3xl text-4xl font-semibold tracking-[-0.05em] text-[var(--text-strong)] sm:text-5xl">
                Vamos transformar a ideia em um produto que parece inevitável.
              </h2>

              <p class="mt-5 max-w-2xl text-lg leading-8 text-[var(--muted)]">
                Conte o contexto, o objetivo e onde a operação trava hoje.
                A partir disso, desenhamos uma solução clara e viável.
              </p>
            </div>

            <div class="flex flex-col gap-3 lg:col-span-4">
              <a :href="whatsHref" target="_blank" rel="noopener"
                class="primary-button inline-flex h-12 items-center justify-center gap-2 rounded-full px-7 text-sm font-semibold">
                Falar sobre o projeto

                <span aria-hidden="true">
                  →
                </span>
              </a>

              <router-link to="/templates"
                class="secondary-button inline-flex h-12 items-center justify-center rounded-full px-7 text-sm font-semibold">
                Ver templates de solução
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup>
import {
  onBeforeUnmount,
  onMounted,
  reactive,
  ref
} from 'vue'

import project1 from '@/assets/projects/1.png'
import project2 from '@/assets/projects/2.png'
import project3 from '@/assets/projects/3.png'

const whatsHref =
  'https://wa.me/77999631644?text=' +
  encodeURIComponent(
    'Olá! Quero conversar sobre um projeto digital sob medida com a Grivaro Studio.'
  )

const pointer = reactive({
  x: 50,
  y: 20
})

const showScrollTop = ref(false)

let observer = null

const mockMenu = [
  'Overview',
  'Produtos',
  'Operações',
  'Integrações',
  'Analytics'
]

const techBadges = [
  'Vue',
  'Nuxt',
  'TypeScript',
  'Tailwind',
  'NestJS',
  'Postgres',
  'Docker',
  'Java',
  'Spring Boot',
  'Python'
]

const chartBars = [
  34,
  44,
  39,
  58,
  51,
  67,
  62,
  76,
  72,
  86,
  82,
  96
]

const metrics = [
  {
    value: '4–8 sem.',
    label: 'média para um MVP bem definido'
  },
  {
    value: '24/7',
    label: 'base preparada para operação contínua'
  },
  {
    value: '100%',
    label: 'produto desenhado para o contexto'
  }
]

const productHighlights = [
  {
    title: 'UX premium',
    desc: 'Interfaces limpas, rápidas e pensadas para uso real.'
  },
  {
    title: 'Arquitetura sólida',
    desc: 'Base técnica preparada para crescer sem retrabalho.'
  },
  {
    title: 'Entrega objetiva',
    desc: 'Escopo claro, ciclos curtos e evolução contínua.'
  }
]

const heroDock = [
  {
    symbol: 'UX',
    title: 'Design pensado no uso',
    desc: 'Fluxos claros desde o primeiro clique'
  },
  {
    symbol: 'API',
    title: 'Tudo conectado',
    desc: 'Dados e serviços trabalhando juntos'
  },
  {
    symbol: '∞',
    title: 'Base para evoluir',
    desc: 'Arquitetura preparada para crescer'
  }
]

const services = [
  {
    symbol: '01',
    title: 'Produtos digitais',
    desc: 'Aplicações web, SaaS e plataformas criadas do zero com foco em clareza, performance e crescimento.',
    points: [
      'MVP validável',
      'Design de produto',
      'Arquitetura escalável'
    ]
  },
  {
    symbol: '02',
    title: 'Sistemas internos',
    desc: 'Backoffices, painéis e operações digitais para reduzir trabalho manual e organizar processos.',
    points: [
      'Dashboards',
      'Fluxos internos',
      'Permissões e relatórios'
    ]
  },
  {
    symbol: '03',
    title: 'Integrações',
    desc: 'Conexões entre APIs, ERPs, CRMs, WhatsApp, pagamentos e serviços essenciais para a operação.',
    points: [
      'Webhooks',
      'Automação',
      'Sincronização de dados'
    ]
  },
  {
    symbol: '04',
    title: 'Evolução técnica',
    desc: 'Refatoração, melhoria de performance, organização de código e sustentação de produtos existentes.',
    points: [
      'Código legado',
      'Performance',
      'Manutenção evolutiva'
    ]
  }
]

const process = [
  {
    title: 'Diagnóstico',
    desc: 'Entendemos problema, contexto, restrições e impacto esperado antes de pensar em tela.'
  },
  {
    title: 'Plano técnico',
    desc: 'Transformamos contexto em escopo, prioridades, arquitetura e uma ordem clara de execução.'
  },
  {
    title: 'Construção',
    desc: 'Desenvolvimento em ciclos curtos, com entregas visíveis, validação e refinamento contínuo.'
  },
  {
    title: 'Lançamento',
    desc: 'Publicamos, monitoramos e evoluímos com base em uso real, métricas e feedback.'
  }
]

const cases = [
  {
    kicker: 'Mobilidade',
    title: 'VodeBuzu',
    desc: 'Sistema para linhas em tempo real, operação e visualização de dados de mobilidade.',
    wins: [
      'Mapa ao vivo',
      'Painéis operacionais',
      'Integração com APIs'
    ],
    image: project1
  },
  {
    kicker: 'Finanças',
    title: 'ControlEase',
    desc: 'Plataforma para gestão financeira, conciliação e visão clara das movimentações.',
    wins: [
      'Regras automáticas',
      'Relatórios de gestão',
      'Menos trabalho manual'
    ],
    image: project2
  },
  {
    kicker: 'IA & Operação',
    title: 'NexGridCo',
    desc: 'Studio digital para paletas, recursos visuais e ferramentas criativas com experiência rápida e moderna.',
    wins: [
      'IA generativa',
      'Automações inteligentes',
      'Integração com APIs'
    ],
    image: project3
  }
]

const principles = [
  {
    kicker: '01',
    title: 'Menos complexidade',
    desc: 'A solução resolve o problema sem adicionar peso desnecessário à operação.'
  },
  {
    kicker: '02',
    title: 'Mais aderência',
    desc: 'O sistema acompanha o fluxo real do negócio, suas regras e suas prioridades.'
  },
  {
    kicker: '03',
    title: 'Evolução contínua',
    desc: 'A base nasce preparada para novos módulos, integrações e mudanças futuras.'
  }
]

const stackGroups = [
  {
    title: 'Frontend',
    items: [
      'Vue.js',
      'Nuxt',
      'React',
      'TypeScript',
      'JavaScript',
      'Tailwind CSS'
    ]
  },

  {
    title: 'Backend',
    items: [
      'NestJS',
      'Node.js',
      'Java',
      'Spring Boot',
      'Python'
    ]
  },

  {
    title: 'Dados',
    items: [
      'PostgreSQL',
      'SQL',
      'Prisma ORM'
    ]
  },

  {
    title: 'Infraestrutura',
    items: [
      'Linux',
      'Nginx',
      'VPS',
      'Networking',
      'Server Management'
    ]
  },

  {
    title: 'Cloud & Containers',
    items: [
      'AWS',
      'Docker',
      'Cloud',
      'Containerization',
      'Deployments'
    ]
  },

  {
    title: 'DevOps & Observabilidade',
    items: [
      'CI/CD',
      'Git',
      'Monitoring',
      'Logging',
      'Automation'
    ]
  },

  {
    title: 'APIs & Integrações',
    items: [
      'REST APIs',
      'GraphQL',
      'Webhooks',
      'OAuth 2.0',
      'Payment APIs'
    ]
  },

  {
    title: 'Cybersecurity',
    items: [
      'Authentication',
      'Authorization',
      'Encryption',
      'Hardening',
      'Vulnerability Assessment',
      'Penetration Testing'
    ]
  },

  {
    title: 'Análise & Operações',
    items: [
      'Systems Analysis',
      'Requirements Analysis',
      'Troubleshooting',
      'Incident Analysis',
      'Technical Support'
    ]
  }
]

function handlePointerMove(event) {
  const width =
    window.innerWidth || 1

  const height =
    window.innerHeight || 1

  pointer.x =
    Math.max(
      0,
      Math.min(
        100,
        (event.clientX / width) * 100
      )
    )

  pointer.y =
    Math.max(
      0,
      Math.min(
        100,
        (event.clientY / height) * 100
      )
    )
}

function handleScroll() {
  showScrollTop.value =
    window.scrollY > 520
}

function scrollToTop() {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

function setupReveal() {
  const elements =
    document.querySelectorAll(
      '[data-reveal]'
    )

  if (
    window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches
  ) {
    elements.forEach(element => {
      element.classList.add(
        'is-visible'
      )
    })

    return
  }

  observer =
    new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (
            entry.isIntersecting
          ) {
            entry.target.classList.add(
              'is-visible'
            )

            observer?.unobserve(
              entry.target
            )
          }
        })
      },
      {
        threshold: 0.1,
        rootMargin:
          '0px 0px -45px 0px'
      }
    )

  elements.forEach(element =>
    observer.observe(element)
  )
}

onMounted(() => {
  setupReveal()
  handleScroll()

  window.addEventListener(
    'scroll',
    handleScroll,
    {
      passive: true
    }
  )
})

onBeforeUnmount(() => {
  observer?.disconnect()

  window.removeEventListener(
    'scroll',
    handleScroll
  )
})
</script>

<style scoped>
:global(:root) {
  --page: #f5f5f7;
  --surface: rgba(255, 255, 255, 0.90);
  --card: rgba(255, 255, 255, 0.76);
  --chip: rgba(245, 245, 247, 0.92);
  --glass: rgba(255, 255, 255, 0.54);
  --glass-strong: rgba(255, 255, 255, 0.82);

  --text: #27272a;
  --text-strong: #09090b;
  --muted: #66666d;
  --muted-2: #a1a1aa;
  --inverse: #ffffff;

  --border: rgba(24, 24, 27, 0.085);
  --focus: rgba(113, 113, 122, 0.18);

  --shadow-soft:
    0 12px 38px rgba(24, 24, 27, 0.07);

  --shadow-large:
    0 32px 100px rgba(24, 24, 27, 0.105);

  --hero-a: #0a0a0b;
  --hero-b: #7c7f87;

  --orb-one:
    rgba(147, 161, 185, 0.22);

  --orb-two:
    rgba(203, 213, 225, 0.31);

  --orb-three:
    rgba(226, 232, 240, 0.28);

  --cta:
    rgba(255, 255, 255, 0.92);
}

:global(html[data-theme='dark']) {
  --page: #08090b;
  --surface: rgba(15, 16, 19, 0.9);
  --card: rgba(19, 20, 24, 0.76);
  --chip: rgba(26, 27, 31, 0.92);
  --glass: rgba(19, 20, 23, 0.54);
  --glass-strong: rgba(18, 19, 22, 0.84);

  --text: #e4e4e7;
  --text-strong: #fafafa;
  --muted: #a1a1aa;
  --muted-2: #5f6169;
  --inverse: #09090b;

  --border: rgba(255, 255, 255, 0.085);
  --focus: rgba(212, 212, 216, 0.13);

  --shadow-soft:
    0 12px 42px rgba(0, 0, 0, 0.30);

  --shadow-large:
    0 36px 120px rgba(0, 0, 0, 0.5);

  --hero-a: #ffffff;
  --hero-b: #777a84;

  --orb-one:
    rgba(71, 85, 105, 0.23);

  --orb-two:
    rgba(63, 63, 70, 0.28);

  --orb-three:
    rgba(39, 39, 42, 0.28);

  --cta:
    rgba(18, 19, 22, 0.94);
}

:global(html) {
  background:
    var(--page);

  transition:
    background-color 350ms ease;
}

.site-page {
  transition:
    background-color 350ms ease,
    color 350ms ease;
}

/* ambiente */

.ambient-grid {
  opacity: 0.31;

  background-image:
    linear-gradient(to right,
      var(--border) 1px,
      transparent 1px),
    linear-gradient(to bottom,
      var(--border) 1px,
      transparent 1px);

  background-size:
    64px 64px;

  mask-image:
    linear-gradient(to bottom,
      black,
      transparent 78%);
}

.ambient-noise {
  opacity: 0.021;

  background-image:
    url("data:image/svg+xml,%3Csvg viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.85'/%3E%3C/svg%3E");
}

.pointer-glow {
  background:
    radial-gradient(42rem circle at var(--pointer-x) var(--pointer-y),
      var(--orb-one),
      transparent 68%);

  transition:
    background 120ms linear;
}

.ambient-orb {
  position: absolute;

  border-radius:
    9999px;

  filter:
    blur(95px);

  will-change:
    transform;
}

.ambient-orb-one {
  left: -11rem;
  top: 8rem;

  width: 34rem;
  height: 34rem;

  background:
    var(--orb-one);

  animation:
    float-a 14s ease-in-out infinite;
}

.ambient-orb-two {
  right: -12rem;
  top: 42rem;

  width: 38rem;
  height: 38rem;

  background:
    var(--orb-two);

  animation:
    float-b 18s ease-in-out infinite;
}

.ambient-orb-three {
  left: 28%;
  top: 112rem;

  width: 30rem;
  height: 30rem;

  background:
    var(--orb-three);

  animation:
    float-c 21s ease-in-out infinite;
}

/* botões */

.primary-button {
  color:
    var(--inverse);

  background:
    var(--text-strong);

  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.10),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);

  transition:
    transform 300ms ease,
    box-shadow 300ms ease;
}

.primary-button:hover {
  transform:
    translateY(-2px);

  box-shadow:
    0 15px 32px rgba(0, 0, 0, 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.13);
}

.secondary-button {
  color:
    var(--text-strong);

  background:
    var(--glass-strong);

  border:
    1px solid var(--border);

  box-shadow:
    var(--shadow-soft);

  backdrop-filter:
    blur(18px);

  transition:
    transform 300ms ease,
    border-color 300ms ease,
    background-color 300ms ease;
}

.secondary-button:hover {
  transform:
    translateY(-2px);

  border-color:
    color-mix(in srgb,
      var(--text-strong) 18%,
      transparent);
}

.scroll-top-button:hover {
  border-color:
    color-mix(in srgb,
      var(--text-strong) 16%,
      transparent);
}

/* hero */

.hero-gradient {
  background:
    linear-gradient(120deg,
      var(--hero-a) 5%,
      var(--hero-b) 96%);

  -webkit-background-clip:
    text;

  background-clip:
    text;

  color:
    transparent;
}

.hero-halo {
  background:
    var(--orb-one);
}

.device-frame {
  background:
    linear-gradient(135deg,
      color-mix(in srgb,
        var(--text-strong) 16%,
        transparent),
      color-mix(in srgb,
        var(--text-strong) 4%,
        transparent));

  border:
    1px solid var(--border);

  box-shadow:
    var(--shadow-large),
    inset 0 1px 0 rgba(255, 255, 255, 0.10);

  backdrop-filter:
    blur(18px);
}

.dashboard-card {
  background:
    rgba(255, 255, 255, 0.045);

  border:
    1px solid rgba(255, 255, 255, 0.075);

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.025);

  transition:
    background-color 300ms ease,
    transform 300ms ease;
}

.dashboard-card:hover {
  background:
    rgba(255, 255, 255, 0.065);

  transform:
    translateY(-2px);
}

.chart-bar {
  min-height:
    12%;

  background:
    linear-gradient(to top,
      #4b5563,
      #f4f4f5);

  opacity:
    0.9;

  transform-origin:
    bottom;

  animation:
    chart-rise 850ms cubic-bezier(.22, .82, .22, 1) both;
}

.health-ring {
  background:
    conic-gradient(#f4f4f5 0 99.9%,
      rgba(255, 255, 255, .08) 99.9% 100%);

  box-shadow:
    0 0 40px rgba(255, 255, 255, .07);
}

.hero-dock {
  transition:
    transform 400ms ease,
    box-shadow 400ms ease;
}

.hero-dock:hover {
  transform:
    translateY(-3px);

  box-shadow:
    var(--shadow-large);
}

/* rail */

.tech-rail {
  mask-image:
    linear-gradient(to right,
      transparent,
      black 7%,
      black 93%,
      transparent);
}

.tech-track {
  animation:
    marquee 30s linear infinite;
}

.tech-rail:hover .tech-track {
  animation-play-state:
    paused;
}

/* conectores */

.flow-transition {
  height:
    5rem;
}

.flow-line {
  position:
    absolute;

  left:
    50%;

  top:
    0;

  width:
    1px;

  height:
    100%;

  background:
    linear-gradient(to bottom,
      transparent,
      var(--border) 25%,
      var(--border) 70%,
      transparent);
}

.flow-node {
  position:
    absolute;

  left:
    50%;

  top:
    50%;

  width:
    9px;

  height:
    9px;

  border:
    2px solid var(--page);

  border-radius:
    9999px;

  background:
    var(--text-strong);

  box-shadow:
    0 0 0 5px color-mix(in srgb,
      var(--text-strong) 8%,
      transparent);

  transform:
    translate(-50%, -50%);
}

.curve-transition {
  position:
    relative;

  width:
    100%;

  height:
    5.5rem;

  overflow:
    hidden;
}

.curve-transition::before {
  content:
    '';

  position:
    absolute;

  left:
    12%;

  top:
    50%;

  width:
    76%;

  height:
    5rem;

  border-top:
    1px solid var(--border);

  border-radius:
    50%;

  transform:
    translateY(18%);
}

.curve-dot {
  position:
    absolute;

  left:
    50%;

  top:
    2.25rem;

  width:
    8px;

  height:
    8px;

  border-radius:
    9999px;

  background:
    var(--text-strong);

  transform:
    translateX(-50%);
}

/* títulos */

.eyebrow {
  font-size:
    0.72rem;

  line-height:
    1rem;

  font-weight:
    700;

  text-transform:
    uppercase;

  letter-spacing:
    0.18em;

  color:
    var(--muted);
}

.section-title {
  max-width:
    52rem;

  font-size:
    clamp(2.35rem,
      4.7vw,
      4rem);

  line-height:
    1.02;

  font-weight:
    600;

  letter-spacing:
    -0.055em;

  color:
    var(--text-strong);
}

/* cards */

.service-card,
.case-card,
.stack-card {
  backdrop-filter:
    blur(18px);
}

.service-card:hover,
.case-card:hover,
.stack-card:hover {
  transform:
    translateY(-6px);

  border-color:
    color-mix(in srgb,
      var(--text-strong) 14%,
      transparent);

  box-shadow:
    var(--shadow-soft);
}

.card-shine {
  position:
    absolute;

  inset:
    -1px;

  opacity:
    0;

  pointer-events:
    none;

  background:
    radial-gradient(24rem circle at 20% 0%,
      color-mix(in srgb,
        var(--text-strong) 7%,
        transparent),
      transparent 60%);

  transition:
    opacity 450ms ease;
}

.service-card:hover .card-shine {
  opacity:
    1;
}

/* processo */

.process-shell {
  backdrop-filter:
    blur(24px);
}

.process-glow {
  position:
    absolute;

  right:
    -8rem;

  top:
    -8rem;

  width:
    25rem;

  height:
    25rem;

  border-radius:
    9999px;

  background:
    var(--orb-one);

  filter:
    blur(80px);

  opacity:
    0.75;
}

.timeline-line {
  background:
    linear-gradient(to bottom,
      transparent,
      var(--border) 8%,
      var(--border) 90%,
      transparent);
}

/* cases */

.case-overlay {
  background:
    linear-gradient(to top,
      rgba(0, 0, 0, .15),
      transparent 48%);
}

/* escuro */

.dark-panel {
  background:
    radial-gradient(circle at 85% 20%,
      rgba(255, 255, 255, .07),
      transparent 30%),
    #090a0c;

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, .08),
    0 36px 110px rgba(0, 0, 0, .22);
}

.dark-panel-grid {
  opacity:
    0.31;

  background-image:
    linear-gradient(to right,
      rgba(255, 255, 255, .055) 1px,
      transparent 1px),
    linear-gradient(to bottom,
      rgba(255, 255, 255, .055) 1px,
      transparent 1px);

  background-size:
    48px 48px;

  mask-image:
    linear-gradient(to left,
      black,
      transparent 74%);
}

.dark-orb {
  background:
    rgba(255, 255, 255, .07);
}

/* stack */

.tech-pill:hover {
  transform:
    translateY(-2px);

  color:
    var(--text-strong);

  border-color:
    color-mix(in srgb,
      var(--text-strong) 16%,
      transparent);
}

/* ponte stack -> CTA */

.stack-cta-bridge {
  pointer-events:
    none;
}

.stack-cta-line {
  position:
    absolute;

  left:
    50%;

  top:
    0;

  width:
    1px;

  height:
    100%;

  background:
    linear-gradient(to bottom,
      transparent,
      var(--border) 22%,
      var(--border) 76%,
      transparent);

  transform:
    translateX(-50%);
}

.stack-cta-node {
  position:
    absolute;

  left:
    50%;

  top:
    50%;

  width:
    8px;

  height:
    8px;

  border-radius:
    9999px;

  background:
    var(--text-strong);

  box-shadow:
    0 0 0 5px color-mix(in srgb,
      var(--text-strong) 8%,
      transparent);

  transform:
    translate(-50%, -50%);
}

/* CTA */

.cta-panel {
  background:
    linear-gradient(135deg,
      var(--cta),
      color-mix(in srgb,
        var(--cta) 85%,
        var(--page)));

  backdrop-filter:
    blur(24px);
}

.cta-orb {
  background:
    var(--orb-one);
}

/* reveal */

[data-reveal] {
  opacity:
    0;

  transform:
    translateY(26px) scale(.992);

  transition:
    opacity 780ms cubic-bezier(.22, .82, .22, 1),
    transform 780ms cubic-bezier(.22, .82, .22, 1);
}

[data-reveal].is-visible {
  opacity:
    1;

  transform:
    translateY(0) scale(1);
}

/* botão topo */

.scroll-top-enter-active,
.scroll-top-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.scroll-top-enter-from,
.scroll-top-leave-to {
  opacity:
    0;

  transform:
    translateY(12px) scale(0.92);
}

/* animações */

@keyframes marquee {
  from {
    transform:
      translateX(0);
  }

  to {
    transform:
      translateX(calc(-50% - 0.375rem));
  }
}

@keyframes chart-rise {
  from {
    transform:
      scaleY(0.08);

    opacity:
      0;
  }

  to {
    transform:
      scaleY(1);

    opacity:
      0.9;
  }
}

@keyframes float-a {

  0%,
  100% {
    transform:
      translate3d(0,
        0,
        0);
  }

  50% {
    transform:
      translate3d(3rem,
        2rem,
        0);
  }
}

@keyframes float-b {

  0%,
  100% {
    transform:
      translate3d(0,
        0,
        0);
  }

  50% {
    transform:
      translate3d(-3rem,
        -2rem,
        0);
  }
}

@keyframes float-c {

  0%,
  100% {
    transform:
      translate3d(0,
        0,
        0);
  }

  50% {
    transform:
      translate3d(1rem,
        -3rem,
        0);
  }
}

@media (max-width: 640px) {
  .ambient-grid {
    background-size:
      44px 44px;
  }

  .curve-transition::before {
    left:
      2%;

    width:
      96%;
  }
}

@media (prefers-reduced-motion: reduce) {

  *,
  *::before,
  *::after {
    scroll-behavior:
      auto !important;

    animation-duration:
      0.01ms !important;

    animation-iteration-count:
      1 !important;

    transition-duration:
      0.01ms !important;
  }

  [data-reveal] {
    opacity:
      1;

    transform:
      none;
  }
}
</style>
